import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiBell, FiUser, FiLogOut, FiSettings, FiClock, FiCheck } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import notificationService from '../services/notificationService';
import { useNotificationSocket } from '../hooks/useNotificationSocket';
import './Header.css';

const Header = () => {
  const { user, logout, subscription } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const notificationDropdownRef = useRef(null);

  const fetchUnreadCount = async () => {
    try {
      const res = await notificationService.getUnreadCount();
      const count = res?.count ?? 0;
      setUnreadCount(count);
    } catch {
      setUnreadCount(0);
    }
  };

  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const res = await notificationService.getList({ limit: 20 });
      const list = res?.data ?? [];
      setNotifications(list);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  useNotificationSocket((notification) => {
    setNotifications((prev) => [notification, ...prev]);
    setUnreadCount((c) => c + 1);
  });

  useEffect(() => {
    if (showNotificationDropdown) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [showNotificationDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(e.target)) {
        setShowNotificationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (n) => {
    if (!n.read) {
      try {
        await notificationService.markRead(n._id);
        setNotifications((prev) => prev.map((x) => (x._id === n._id ? { ...x, read: true } : x)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // ignore
      }
    }
    setShowNotificationDropdown(false);
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark all as read');
      fetchUnreadCount();
    }
  };

  const subscriptionDays = (() => {
    if (!subscription?.hasAccess) return null;
    const now = new Date();
    const endDate =
      subscription.subscriptionStatus === 'trial'
        ? subscription.trialEndsAt
        : subscription.subscriptionEndsAt;
    if (!endDate) return null;
    const end = new Date(endDate);
    const daysLeft = Math.ceil((end - now) / (24 * 60 * 60 * 1000));
    const label = subscription.subscriptionStatus === 'trial' ? 'Trial' : 'Plan';
    if (daysLeft <= 0) return { label, text: 'Expires today' };
    return {
      label,
      text: daysLeft === 1 ? '1 day left' : `${daysLeft} days left`,
    };
  })();

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <h1 className="page-title">Welcome back, {user?.name}! 👋</h1>
          {subscriptionDays && (
            <span className="subscription-days-badge">
              <FiClock />
              <span className="subscription-days-label">{subscriptionDays.label}:</span>
              <span className="subscription-days-count">{subscriptionDays.text}</span>
            </span>
          )}
        </div>

        <div className="header-right">
          <div className="notification-wrap" ref={notificationDropdownRef}>
            <button
              type="button"
              className="icon-button notification-button"
              onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
              aria-label="Notifications"
            >
              <FiBell />
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {showNotificationDropdown && (
              <div className="notification-dropdown">
                <div className="notification-dropdown-header">
                  <span className="notification-dropdown-title">Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      className="notification-mark-all"
                      onClick={handleMarkAllRead}
                    >
                      <FiCheck /> Mark all read
                    </button>
                  )}
                </div>
                <div className="notification-list">
                  {notificationsLoading ? (
                    <div className="notification-empty">Loading…</div>
                  ) : notifications.length === 0 ? (
                    <div className="notification-empty">No notifications</div>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n._id}
                        type="button"
                        className={`notification-item ${n.read ? 'read' : ''}`}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className="notification-item-title">{n.title}</div>
                        {n.message && (
                          <div className="notification-item-message">{n.message}</div>
                        )}
                        <div className="notification-item-time">
                          {n.createdAt
                            ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })
                            : ''}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="user-menu">
            <button
              type="button"
              className="user-button"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="user-avatar">
                <FiUser />
              </div>
              <div className="user-info">
                <span className="user-name">{user?.name}</span>
                <span className="user-role">Shop Admin</span>
              </div>
            </button>

            {showDropdown && (
              <div className="dropdown-menu">
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    navigate('/settings');
                    setShowDropdown(false);
                  }}
                >
                  <FiSettings />
                  <span>Settings</span>
                </button>
                <button type="button" className="dropdown-item" onClick={logout}>
                  <FiLogOut />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
